import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Row, Col, Popconfirm } from 'antd';
import { Button, Input, Select } from 'antd';
import { Card, Table, Dropdown, Typography, Tag, Space, message } from 'antd';
import {
  CheckCircleOutlined,
  BarChartOutlined,
  ReloadOutlined,
  SwapLeftOutlined,
  DownloadOutlined,
  FormOutlined,
  PlusCircleOutlined,
  CheckOutlined,
  MinusCircleOutlined,
  MoreOutlined,
  ApartmentOutlined,
  BankOutlined,
} from '@ant-design/icons';
import BasicLayout from '@layout/basic';
import API from '@/api/api';
import { useProjectSitesData } from '@/hooks/useProjectSitesData'; // 특화 훅 import
import { CycleStatus, StatusColor, CycleStatusOrderIndex } from '@constants/status';
import * as XLSX from 'xlsx';

const { Title } = Typography;

export default function AdminEstimateProjectSites() {
  const { project_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const project_code = searchParams.get('project_code');
  const site_pgnm = searchParams.get('site_pgnm');

  // 특화 훅으로 데이터 로직 대체
  // const [loading, setLoading] = useState(true);
  const [ActionKeyInWorking, setActionKeyInWorking] = useState(false);

  // const [sites, setSites] = useState([]);
  // const [filteredSites, setFilteredSites] = useState([]);
  // const [RequiredDocuments, setRequiredDocuments] = useState([]);
  // const [OptionalDocuments, setOptionalDocuments] = useState([]);
  // const [AttachmentColumnWidth, setAttachmentColumnWidth] = useState(0);
  // const [currentSiteTablePage, setCurrentSiteTablePage] = useState(1);
  const {
    sites,
    requiredDocuments,
    optionalDocuments,
    attachmentColumnWidth,
    loading,
    currentSiteTablePage,
    setCurrentSiteTablePage,
    refetch,
  } = useProjectSitesData(project_id, project_code, site_pgnm);

  const [filteredSites, setFilteredSites] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    companyName: '',
    siteStatus: '',
    siteName: '',
    cycleStatus: '',
  });

  // 검색 필터링 함수 (컴포넌트에 유지)
  const applyFilters = useCallback(() => {
    let filtered = sites;
    if (searchFilters.companyName.trim()) {
      filtered = filtered.filter((site) =>
        site.company_name?.toLowerCase().includes(searchFilters.companyName.toLowerCase())
      );
    }
    if (searchFilters.siteStatus !== '') {
      const isUsed = searchFilters.siteStatus === 'true';
      filtered = filtered.filter((site) => site.used === isUsed);
    }
    if (searchFilters.siteName.trim()) {
      filtered = filtered.filter((site) =>
        site.site_name?.toLowerCase().includes(searchFilters.siteName.toLowerCase())
      );
    }
    if (searchFilters.cycleStatus !== '') {
      filtered = filtered.filter(({ cycle_status }) => cycle_status === searchFilters.cycleStatus);
    }
    setFilteredSites(filtered);
  }, [sites, searchFilters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const updateFilter = (key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setSearchFilters({ companyName: '', siteStatus: '', siteName: '', cycleStatus: '' });
  };

  // 엑셀 다운로드 함수 (컴포넌트에 유지)
  const downloadExcel = () => {
    if (!filteredSites.length) {
      message.warning('다운로드할 데이터가 없습니다.');
      return;
    }
    try {
      const excelData = filteredSites.map((site, index) => {
        const baseData = {
          순번: index + 1,
          기업ID: site.company_id || '',
          기업명: site.company_name || '',
          참여유무: site.used ? '참여' : '미참여',
          사업장ID: site.site_id || '',
          사업장명: site.site_name || '',
          진행상태: site.site_status || '',
          최근회차: site.latest_cycle_index ? `${site.latest_cycle_index}회차` : '',
          '최근회차 상태': site.cycle_status ? `${CycleStatus[site.cycle_status]}` : '',
        };
        requiredDocuments.forEach((doc) => {
          const key = `필수_${doc.type_name_ko}(${doc.document_type})`;
          baseData[key] = site.attachment_status?.[doc.document_type] ? 'O' : 'X';
        });
        optionalDocuments.forEach((doc) => {
          const key = `증빙_${doc.type_name_ko}(${doc.document_type})`;
          baseData[key] = site.attachment_status?.[doc.document_type] ? 'O' : 'X';
        });
        return baseData;
      });
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const columnWidths = [
        { wch: 8 },
        { wch: 12 },
        { wch: 20 },
        { wch: 12 },
        { wch: 12 },
        { wch: 25 },
        { wch: 12 },
        { wch: 12 },
        ...Array(requiredDocuments.length + optionalDocuments.length).fill({ wch: 15 }),
      ];
      worksheet['!cols'] = columnWidths;
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '사업장목록');
      const fileName = `${project_code}_사업장목록_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      message.success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      message.error('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

// 최신 제출 및 피드백 내역 엑셀 다운로드 함수
const downloadLatestSubmissionFeedbackExcel = async () => {
  try {
    const response = await API.get(`/projects/${project_id}/feedback-upload-results`, {
      params: { format: 'excel' },
      responseType: 'blob',
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = '';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch?.[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        const fallbackMatch = contentDisposition.match(/filename="(.+)"/);
        if (fallbackMatch?.[1]) {
          filename = fallbackMatch[1];
        }
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    message.success('엑셀 파일이 다운로드되었습니다.');
  } catch (error) {
    console.error('엑셀 다운로드 오류:', error);
    message.error('엑셀 다운로드 중 오류가 발생했습니다.');
  }
};


  function GetDocumentIconColor(used, isUploaded, isFeedbacked) {
    return {
      attachment: !used ? '#989898' : isUploaded ? '#096dd9' : '#f5222d',
      feedback: !used || !isUploaded ? '#989898' : isFeedbacked ? '#096dd9' : '#f5222d',
    };
  }

  function PrintCell(val, children) {
    if (val === false) {
      return <div className="text-center"> ㅡ </div>;
    } else {
      return children;
    }
  }

  async function PartipateInProjectByAdmin(record) {
    try {
      await API.post(
        `/admin/estimates/projects/${record.project_id}/companies/${record.company_id}`
      );
      message.success(`대상사 - "${record.company_name}" 의 참여처리를 완료하였습니다.`);
    } catch (error) {
      message.error(`대상사 - "${record.company_name}" 의 참여처리 도중 장애가 발생했습니다.`);
    } finally {
      await refetch();
      setActionKeyInWorking(null);
    }
  }

  // 미참여 처리 (미개발)
  // async function UnPartipateInProjectByAdmin(record) {
  //   try {
  //     console.log(`UnPartipateInProjectByAdmin ~ `);
  //     console.log(` ~ record => `, record);

  //     // await API.post(
  //     //   `/admin/estimates/projects/${record.project_id}/companies/${record.company_id}`
  //     // );
  //     // message.success(`대상사 - "${record.company_name}" 의 참여처리를 완료하였습니다.`);
  //   } catch (error) {
  //     message.error(`대상사 - "${record.company_name}" 의 미참여 처리 도중 장애가 발생했습니다.`);
  //   } finally {
  //     await refetch();
  //     setActionKeyInWorking(null);
  //   }
  // }

  async function deleteSite(record, is_delete_site) {
    try {
      await API.delete(`/admin/sites/${record?.site_id}`, {
        params: { project_id: record.project_id, is_delete_site },
      });
      message.success(`협력사 - "${record.site_name}"을 삭제 완료하였습니다.`);
    } catch (error) {
      message.success(`죄송합니다. 협력사 - "${record.site_name}"을 삭제 중 장애가 발생했습니다.`);
    } finally {
      await refetch();
      setActionKeyInWorking(null);
    }
  }

  async function CreateSiteByAdmin(record) {
    try {
      await API.post(`/sites`, {
        site_name: `${record.company_name} - 사업장`,
        company_id: record.company_id,
        project_id: record.project_id,
      });
      message.success(`대상사 - "${record.company_name}" 의 사업장 생성을 완료하였습니다.`);
    } catch (error) {
      message.error(`대상사 - "${record.company_name}" 의 사업장 생성 도중 장애가 발생했습니다.`);
    } finally {
      await refetch();
      setActionKeyInWorking(null);
    }
  }

  async function deleteParticipatedCompany(record, is_delete_company) {
    try {
      await API.delete(`/admin/companies/${record.company_id}`, {
        params: { project_id: record.project_id, is_delete_company },
      });
    } catch (error) {
      let error_message = null;
      if (error?.response) {
        const { status_code, detail } = error.response;
        if (status_code == 500) {
          error_message = detail;
        } else if (status_code === 404 && detail === 'Not_Found_Company') {
          error_message = '삭제하려는 참여기업이 조회되지 않습니다.';
        }
      }

      if (!error_message) {
        error_message = '참여기업을 조회 및 삭제하는 도중 장애가 발생했습니다.';
      }

      message.error(error_message);
    } finally {
      await refetch();
      setActionKeyInWorking(null);
    }
  }

  function ProjectSiteAction(record) {
    let ActionMenus = [];
    let loadingActionkey = `company-${record.company_id}`;
    if (record.site_id) {
      loadingActionkey += `-site-${record.site_id}`;
    }
    if (!record.used) {
      // 상태: 프로젝트 미참여
      ActionMenus.push({
        key: `control-company-${record.company_id}-participated`,
        icon: <BarChartOutlined />,
        label: (
          <Popconfirm
            title={
              <span className="fs-6">
                현재 프로젝트 내 해당 참여기업을 참여상태로 전환합니다. <br />
                참여처리 하시겠습니까?
              </span>
            }
            okText="참여처리"
            cancelText="취소"
            okType="primary"
            onConfirm={() => {
              setActionKeyInWorking(loadingActionkey);
              PartipateInProjectByAdmin(record);
            }}
          >
            <span>참여처리</span>
          </Popconfirm>
        ),
      });
    } else {
      if (!record.site_id) {
        // 상태: 프로젝트 참여 && 사업장 미생성
        ActionMenus.push(
          {
            key: `control-company-${record.company_id}-generate-site`,
            icon: <PlusCircleOutlined />,
            label: (
              <Popconfirm
                title={
                  <span className="fs-6">
                    해당 기업의 신규 사업장을 임의 생성 후 관리자가 증빙서류 제출, 피드백 작성 등을
                    할 수 있습니다. <br />
                    생성하시겠습니까?
                  </span>
                }
                okText="생성"
                cancelText="취소"
                okType="primary"
                onConfirm={() => {
                  setActionKeyInWorking(loadingActionkey);
                  CreateSiteByAdmin(record);
                }}
              >
                <span>사업장 생성</span>
              </Popconfirm>
            ),
          }
          // 미참여 처리 (미개발)
          // {
          //   key: `control-company-${record.company_id}-unparticipated`,
          //   icon: <BarChartOutlined />,
          //   label: (
          //     <Popconfirm
          //       title={
          //         <span className="fs-6">
          //           현재 프로젝트 내 해당 참여기업을 미참여 상태로 전환합니다. <br />
          //           미참여 처리 하시겠습니까?
          //         </span>
          //       }
          //       okText="미참여 처리"
          //       cancelText="취소"
          //       okType="primary"
          //       onConfirm={() => {
          //         setActionKeyInWorking(loadingActionkey);
          //         UnPartipateInProjectByAdmin(record);
          //       }}
          //     >
          //       <span>미참여 처리</span>
          //     </Popconfirm>
          //   ),
          //   disabled: !record.used,
          // }
        );
      } else {
        // 상태: 프로젝트 참여 && 사업장 생성됨
        ActionMenus.push(
          {
            key: `control-company-${record.company_id}-estimate-site-${record.site_id}`,
            icon: <CheckOutlined />,
            label: '산정하기',
            onClick: () =>
              navigate(
                `/admin/estimates/projects/${project_id}/companies/${record.company_id}/sites/${record.site_id}/estimates/${record.estimate_site_info_id}?project_code=${project_code}&site_name=${encodeURIComponent(record.site_name)}&site_pgnm=${currentSiteTablePage}`
              ),
          },
          {
            key: `control-project-${project_id}-company-${record.company_id}-site-${record.site_id}`,
            label: '사업장 관리',
            children: [
              {
                key: `control-project-${project_id}-company-${record.company_id}-site-${record.site_id}-delete`,
                icon: (
                  <>
                    <ApartmentOutlined className="me-1 text-danger" />
                    <MinusCircleOutlined className="me-1 text-danger" />
                  </>
                ),
                label: (
                  <Popconfirm
                    title={
                      <span className="fs-6">
                        해당 사업장의 현재 프로젝트 참여 데이터(참여내역 및 증빙서류 등)를
                        삭제합니다. <br />
                        삭제하시겠습니까?
                      </span>
                    }
                    okText="삭제"
                    cancelText="취소"
                    okType="danger"
                    onConfirm={() => {
                      setActionKeyInWorking(loadingActionkey);
                      deleteSite(record, false);
                    }}
                  >
                    <span>사업장 - 현재 프로젝트 참여내역 삭제</span>
                  </Popconfirm>
                ),
              },
              {
                key: `control-company-${record.company_id}-site-${record.site_id}-delete`,
                icon: (
                  <>
                    <ApartmentOutlined className="me-1 text-danger" />
                    <MinusCircleOutlined className="me-1 text-danger" />
                  </>
                ),
                label: (
                  <Popconfirm
                    title={
                      <span className="fs-6">
                        해당 사업장의 기본 정보 및 참여한 모든 프로젝트 내 데이터(참여내역 및
                        증빙서류 등)를 삭제합니다. <br />
                        삭제하시겠습니까?
                      </span>
                    }
                    okText="삭제"
                    cancelText="취소"
                    okType="danger"
                    onConfirm={() => {
                      setActionKeyInWorking(loadingActionkey);
                      deleteSite(record, true);
                    }}
                  >
                    <span>사업장 - 데이터 전체 삭제</span>
                  </Popconfirm>
                ),
              },
            ],
          }
        );
      }

      ActionMenus.push({
        key: `control-project-${project_id}-company-${record.company_id}-${record.index}`,
        label: '참여기업 관리',
        children: [
          {
            key: `control-project-${project_id}-company-${record.company_id}-delete`,
            icon: (
              <>
                <BankOutlined className="me-1 text-danger" />
                <MinusCircleOutlined className="me-1 text-danger" />
              </>
            ),
            label: (
              <Popconfirm
                title={
                  <span className="fs-6">
                    해당 기업의 현재 프로젝트 내 참여 데이터(참여내역 및 증빙서류 등)를 삭제합니다.{' '}
                    <br />
                    삭제하시겠습니까?
                  </span>
                }
                okText="삭제"
                cancelText="취소"
                okType="danger"
                onConfirm={() => {
                  setActionKeyInWorking(loadingActionkey);
                  deleteParticipatedCompany(record, false);
                }}
              >
                <span>참여기업 - 현재 프로젝트 참여내역 삭제</span>
              </Popconfirm>
            ),
          },
          {
            key: `control-company-${record.company_id}-delete`,
            icon: (
              <>
                <BankOutlined className="me-1 text-danger" />
                <MinusCircleOutlined className="me-1 text-danger" />
              </>
            ),
            label: (
              <Popconfirm
                title={
                  <span className="fs-6">
                    해당 기업의 기본 정보 및 참여한 모든 프로젝트 내 데이터(참여내역 및 증빙서류
                    등)를 삭제합니다. <br />
                    삭제하시겠습니까?
                  </span>
                }
                okText="삭제"
                cancelText="취소"
                okType="danger"
                onConfirm={() => {
                  setActionKeyInWorking(loadingActionkey);
                  deleteParticipatedCompany(record, true);
                }}
              >
                <span>참여기업 - 데이터 전체 삭제</span>
              </Popconfirm>
            ),
          },
        ],
      });
    }

    return (
      <Dropdown
        menu={{
          items: ActionMenus,
        }}
        trigger={['click']}
        loading={ActionKeyInWorking == loadingActionkey}
        disabled={ActionKeyInWorking}
      >
        <Button icon={<MoreOutlined />}>관리하기</Button>
      </Dropdown>
    );
  }

  return (
    <BasicLayout>
      <Row className="mb-2">
        <Col xs={24} lg={24}>
          <div className="d-flex align-items-center justify-content-between mb-1">
            <Link className="text-gray-7 hover-text-blue-8 fs-6" to={`/admin/estimates/projects`}>
              <SwapLeftOutlined className="me-2" />
              산정 프로젝트 전체보기
            </Link>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={loading}>
              새로고침
            </Button>
          </div>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={24} lg={24}>
          <Title level={2}>{project_code} 프로젝트 - 사업장 목록</Title>
        </Col>
      </Row>
      <Card
        title="참여 사업장 목록"
        extra={
          <div>
            <Space>
              <Button onClick={resetFilters}>필터 초기화</Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadExcel}
                disabled={!filteredSites.length}
              >
                엑셀 다운로드
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadLatestSubmissionFeedbackExcel}
                disabled={!filteredSites.length}
              >
                최신 제출/피드백 내역 다운로드
              </Button>
            </Space>
          </div>
        }
        bodyStyle={{ padding: '10px' }}
      >
        <Row className="mt-1 mb-2">
          <Col xs={24}>
            <div className="d-flex justify-content-end">
              <div className="d-flex align-items-center border p-2 me-2">
                <strong className="me-2" style={{ fontSize: '15px' }}>
                  첨부 상태 -
                </strong>
                <div className="d-flex me-2">
                  <div className="me-1" style={{ fontSize: '14px' }}>
                    첨부완료:{' '}
                  </div>
                  <CheckCircleOutlined style={{ color: '#096dd9', fontSize: '21px' }} />
                </div>

                <div className="d-flex me-2">
                  <div className="me-1" style={{ fontSize: '14px' }}>
                    미첨부:{' '}
                  </div>
                  <CheckCircleOutlined style={{ color: '#f5222d', fontSize: '21px' }} />
                </div>

                <div className="d-flex">
                  <div className="me-1" style={{ fontSize: '14px' }}>
                    미참여:{' '}
                  </div>
                  <CheckCircleOutlined style={{ color: '#989898', fontSize: '21px' }} />
                </div>
              </div>
              <div className="d-flex align-items-center border p-2">
                <strong className="me-2" style={{ fontSize: '15px' }}>
                  피드백 상태 -
                </strong>
                <div className="d-flex me-2">
                  <div className="me-1" style={{ fontSize: '14px' }}>
                    작성완료:{' '}
                  </div>
                  <FormOutlined style={{ color: '#096dd9', fontSize: '21px' }} />
                </div>

                <div className="d-flex me-2">
                  <div className="me-1" style={{ fontSize: '14px' }}>
                    미작성:{' '}
                  </div>
                  <FormOutlined style={{ color: '#f5222d', fontSize: '21px' }} />
                </div>

                <div className="d-flex me-2">
                  <div className="me-1" style={{ fontSize: '14px' }}>
                    미참여:{' '}
                  </div>
                  <FormOutlined style={{ color: '#989898', fontSize: '21px' }} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <Row className="">
          <Col xs={24}>
            <Card className="bg-light mb-3">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8} md={6}>
                  <div>
                    <label className="fw-medium mb-1 d-block">기업명</label>
                    <Input
                      placeholder="기업명으로 검색"
                      value={searchFilters.companyName}
                      onChange={(e) => updateFilter('companyName', e.target.value)}
                      allowClear
                    />
                  </div>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <div>
                    <label className="fw-medium mb-1 d-block">사업장명</label>
                    <Input
                      placeholder="사업장명으로 검색"
                      value={searchFilters.siteName}
                      onChange={(e) => updateFilter('siteName', e.target.value)}
                      allowClear
                    />
                  </div>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <div>
                    <label className="fw-medium mb-1 d-block">참여유무</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="참여유무 선택"
                      value={searchFilters.siteStatus || undefined}
                      onChange={(value) => updateFilter('siteStatus', value || '')}
                      allowClear
                    >
                      <Select.Option value="true">참여</Select.Option>
                      <Select.Option value="false">미참여</Select.Option>
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <div>
                    <label className="fw-medium mb-1 d-block">진행상태</label>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="진행상태 선택"
                      value={searchFilters.cycleStatus || undefined}
                      onChange={(value) => updateFilter('cycleStatus', value || '')}
                      allowClear
                    >
                      {Object.entries(CycleStatus).map(([state, label]) => (
                        <Select.Option key={`select-cycle-status-${state}`} value={state}>
                          {label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xs={24}>
            <Table
              dataSource={filteredSites}
              loading={loading}
              rowKey={(record) =>
                `projects-${project_id}-company-${record.company_id}-site-${record.site_id}`
              }
              pagination={{
                current: currentSiteTablePage,
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} 사업장`,
                onChange: (page) => setCurrentSiteTablePage(page),
              }}
              style={{ width: '100%' }}
              bordered
            >
              <Table.Column
                title={<div className={'fw-medium fs-6 text-center'}>{'기업명'}</div>}
                dataIndex="company_name"
                key="company_name"
                width={'10%'}
                sorter={(a, b) => a.company_name.localeCompare(b.company_name)}
                render={(company_name, record) => (
                  <>
                    <Tag>{record.company_id}</Tag>
                    <span style={{ fontSize: '13px' }}>{company_name}</span>
                  </>
                )}
              />

              <Table.Column
                title={<span className={'fw-medium fs-6'}>{'참여유무'}</span>}
                dataIndex="used"
                key="used"
                align="center"
                width={'7%'}
                sorter={(a, b) => (a.used === b.used ? 0 : a.used ? 1 : -1)}
                render={(used) => (
                  <CheckCircleOutlined
                    style={{ color: used ? '#096dd9' : '#f5222d', fontSize: '21px' }}
                  />
                )}
              />

              <Table.Column
                title={<span className={'fw-medium fs-6'}>{'사업장명'}</span>}
                dataIndex="site_name"
                key="site_name"
                width={'13.5%'}
                align="center"
                sorter={(a, b) => a.site_name.localeCompare(b.site_name)}
                render={(site_name, record) =>
                  PrintCell(
                    record.used &&
                      record.site_id !== null &&
                      record.company_status !== 'NOT_STARTED',
                    <div className="text-start">
                      <Tag>{record.site_id}</Tag>
                      <span style={{ fontSize: '13px' }}>{site_name}</span>
                    </div>
                  )
                }
              />

              <Table.Column
                title={<span className={'fw-medium fs-6'}>{'최근회차'}</span>}
                dataIndex="latest_cycle_index"
                key="latest_cycle_index"
                align="center"
                width={'7.5%'}
                sorter={(a, b) => a.latest_cycle_index - b.latest_cycle_index}
                render={(latest_cycle_index, record) =>
                  PrintCell(
                    record.used &&
                      record.site_id !== null &&
                      record.company_status !== 'NOT_STARTED',
                    <span style={{ fontSize: '15px' }}>{latest_cycle_index} 회차</span>
                  )
                }
              />

              <Table.Column
                title={<span className={'fw-medium fs-6'}>{'진행상태'}</span>}
                dataIndex="cycle_status"
                key="cycle_status"
                align="center"
                width={'7.5%'}
                sorter={(a, b) =>
                  CycleStatusOrderIndex[a.cycle_status] - CycleStatusOrderIndex[b.cycle_status]
                }
                render={(cycle_status, record) =>
                  PrintCell(
                    record.used &&
                      record.site_id !== null &&
                      record.company_status !== 'NOT_STARTED',
                    <Tag color={StatusColor[cycle_status]}>{CycleStatus[cycle_status]}</Tag>
                  )
                }
              />

              {requiredDocuments.length > 0 && (
                <Table.ColumnGroup
                  title={<span className={'fw-medium fs-6'}>{'필수 증빙서류'}</span>}
                  className="text-center"
                >
                  {requiredDocuments.map((document) => (
                    <Table.Column
                      key={document?.id ?? document?.document_type_id}
                      title={
                        <span className={'fw-medium'}>
                          {document.type_name_ko}({document.document_type})
                        </span>
                      }
                      dataIndex={document?.id ?? document?.document_type_id}
                      align="center"
                      width={attachmentColumnWidth}
                      sorter={(a, b) => {
                        const aUploaded = a.attachment_status?.[document.document_type] || false;
                        const bUploaded = b.attachment_status?.[document.document_type] || false;
                        return aUploaded === bUploaded ? 0 : aUploaded ? 1 : -1;
                      }}
                      render={(_, record) => {
                        const isUploaded = record.attachment_status?.[document.document_type];
                        const isFeedbacked = record.feedback_status?.[document.document_type];
                        const IconColor = GetDocumentIconColor(
                          record.used && record.site_id,
                          isUploaded,
                          isFeedbacked
                        );
                        const UploadedIcon = (
                          <CheckCircleOutlined
                            className="me-3"
                            style={{ color: IconColor.attachment, fontSize: '19px' }}
                          />
                        );
                        const FeedbackedIcon = (
                          <FormOutlined style={{ color: IconColor.feedback, fontSize: '19px' }} />
                        );
                        return (
                          <div className="d-flex align-items-center justify-content-center">
                            {UploadedIcon}
                            {FeedbackedIcon}
                          </div>
                        );
                      }}
                    ></Table.Column>
                  ))}
                </Table.ColumnGroup>
              )}

              {optionalDocuments.length > 0 && (
                <Table.ColumnGroup
                  title={<span className={'fw-medium fs-6'}>{'증빙서류'}</span>}
                  className="text-center"
                >
                  {optionalDocuments.map((document) => (
                    <Table.Column
                      key={document?.id ?? document?.document_type_id}
                      title={<span className={'fw-medium'}>{document.type_name_ko}</span>}
                      dataIndex={document?.id ?? document?.document_type_id}
                      align="center"
                      width={attachmentColumnWidth}
                      sorter={(a, b) => {
                        const aUploaded = a.attachment_status?.[document.document_type] || false;
                        const bUploaded = b.attachment_status?.[document.document_type] || false;
                        return aUploaded === bUploaded ? 0 : aUploaded ? 1 : -1;
                      }}
                      render={(_, record) => {
                        const isUploaded = record.attachment_status?.[document.document_type];
                        const isFeedbacked = record.feedback_status?.[document.document_type];
                        const IconColor = GetDocumentIconColor(
                          record.used && record.site_id,
                          isUploaded,
                          isFeedbacked
                        );
                        const UploadedIcon = (
                          <CheckCircleOutlined
                            className="me-3"
                            style={{ color: IconColor.attachment, fontSize: '19px' }}
                          />
                        );
                        const FeedbackedIcon = (
                          <FormOutlined style={{ color: IconColor.feedback, fontSize: '19px' }} />
                        );
                        return (
                          <div className="d-flex align-items-center justify-content-center">
                            {UploadedIcon}
                            {FeedbackedIcon}
                          </div>
                        );
                      }}
                    ></Table.Column>
                  ))}
                </Table.ColumnGroup>
              )}

              <Table.Column
                title={'산정하기'}
                key="action"
                align="center"
                width={'8.5%'}
                render={(_, record) => ProjectSiteAction(record)}
              />
            </Table>
          </Col>
        </Row>
      </Card>
    </BasicLayout>
  );
}
